import { Page } from "components/Page"
import { useTranslator } from "components/Translator"
import { Hero } from "components/Hero"
import { Wrapper } from "components/Wrapper"
import { TwoColLayout } from "components/TwoColLayout"
import { Fragment } from "react"
import { Text } from "components/Text"
import { ChevronRight } from "components/Icon"
import { useConnectedUser } from "components/ConnectedUserProvider"
import { AutoForm, Root } from "components/Form/AutoForm"
import { useRouter } from "components/Router"
import { PlusSquareRegular, Comments } from "components/Icon"
import { Button } from "components/Button"
import { Input } from "./Input"
import { MenuButton } from "components/Menu"
import { Search } from "components/Icon"
import { getInitialValue } from "./Form"

export const ForumPage = (props) => {
  const translator = useTranslator()
  const router = useRouter()

  const tag = props.tag ?? props.post?.tag ?? null

  return (
    <Page>
      <Hero
        backgroundImage={tag?.imageFile?.thumbs?.w1920}
        title={props.title}
      />
      <Wrapper size={"large"}>
        <div className={"grid gap-4"}>
          <Breadcrumbs
            label={translator.trans(
              "club.forum.common.breadcrumbs.label",
              null,
              "pages",
            )}
            items={[
              {
                href: router.generate("app_club_forum_index"),
                label: translator.trans("club.forum.index.breadcrumbsLabel"),
              },
              tag
                ? {
                    href: router.generate("app_club_forum_tag", {
                      forumTagSlug: tag.slug,
                    }),
                    label: tag.title,
                  }
                : null,
              props.post
                ? {
                    href: router.generate("app_club_forum_post", {
                      forumTagSlug:
                        props.post.parentPost?.tag.slug ?? props.post.tag.slug,
                      forumPostSlug:
                        props.post.parentPost?.slug ?? props.post.slug,
                    }),
                    label: props.post.parentPost?.title ?? props.post.title,
                  }
                : null,
              ...(props.breadcrumbsItems ?? []),
            ].filter((item) => item !== null)}
          />
          <TwoColLayout
            main={
              <Main formSearch={props.formSearch} metrics={props.metrics}>
                {props.main}
              </Main>
            }
            sidebar={
              <Sidebar tag={props.tag} post={props.post}>
                {props.sidebar}
              </Sidebar>
            }
          />
        </div>
      </Wrapper>
    </Page>
  )
}

const Breadcrumbs = (props) => {
  return (
    <nav aria-label={props.label} className={"py-4"}>
      <ol className={"flex items-center gap-2 flex-wrap"}>
        {props.items.map((item, index) => {
          return (
            <Fragment key={item.href}>
              <li>
                <Text tag={"a"} variant={"headline4"} href={item.href}>
                  {item.label}
                </Text>
              </li>
              {index < props.items.length - 1 ? (
                <li aria-hidden="true">
                  <ChevronRight className={"w-4 h-4"} />
                </li>
              ) : null}
            </Fragment>
          )
        })}
      </ol>
    </nav>
  )
}

const Main = (props) => {
  const connectedUser = useConnectedUser()
  const translator = useTranslator()

  const searchParams = new URLSearchParams(window.location.search)

  return (
    <div className={"grid gap-8"}>
      {!connectedUser ? (
        <section className={"grid gap-4"}>
          <Text variant={"headline3"} component={"h2"}>
            {translator.trans("club.forum.common.welcome.title", null, "pages")}
          </Text>
          <Text component={"p"}>
            {translator.trans(
              "club.forum.common.welcome.body",
              { nbUsers: props.metrics?.nbUsers },
              "pages",
            )}
          </Text>
        </section>
      ) : null}
      {props.formSearch ? (
        <AutoForm
          schema={props.formSearch}
          initialValue={{
            [props.formSearch.title]: getInitialValue(props.formSearch, null),
          }}
          submitLabel={translator.trans(
            "club.forum.common.search.submit",
            null,
            "pages",
          )}
        >
          <Root>
            <Input
              name={"forum_post_filter[keyword]"}
              defaultValue={searchParams.get("forum_post_filter[keyword]")}
              placeholder={
                props.formSearch.properties.keyword.options.attr?.placeholder
              }
              endAdornment={
                <MenuButton
                  type={"submit"}
                  color={"light"}
                  illustration={<Search className="w-4" />}
                />
              }
            />
          </Root>
        </AutoForm>
      ) : null}
      {props.children}
    </div>
  )
}

const Sidebar = (props) => {
  const router = useRouter()
  const translator = useTranslator()
  const connectedUser = useConnectedUser()

  return (
    <div className={"flex gap-2 lg:flex-col"}>
      {props.children}
      {props.post ? (
        <Button
          href={"#reply-form"}
          iconLeft={<Comments className={"w-6 h-6"} />}
          color={"secondary"}
          className={"grow basis-full"}
        >
          {translator.trans("club.forum.common.reply", null, "pages")}
        </Button>
      ) : null}
      {connectedUser &&
      (connectedUser.roles.includes("ROLE_CLUB") ||
        connectedUser.roles.includes("ROLE_ADMIN")) ? (
        <Button
          href={router.generate(
            "app_user_forum_add",
            props.tag
              ? {
                  forumTagId: props.tag.id,
                }
              : undefined,
          )}
          iconLeft={<PlusSquareRegular className={"w-6 h-6"} />}
          className={"grow basis-full"}
        >
          {translator.trans("club.forum.tag.add")}
        </Button>
      ) : null}
    </div>
  )
}
